import { CommonLoaderBlue } from '@/components/common/CommonLoader';
import { getImageUrl } from '@/utils/getimageurl';
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

// Swiper imports for inline carousel
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Pagination, Autoplay } from 'swiper/modules';

const SingleNews = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);

  const swiperRef = useRef(null);

  const [openGallery, setOpenGallery] = useState(false);
  const [activeImage, setActiveImage] = useState(null);

  // NEW: autoplay state
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/news/${id}`);
        const data = await res.json();
        data.images = [...data.images, { ...data.images, image: data.banner_image }];
        console.log(data.images);
        setNews(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [id]);

  useEffect(() => {
    if (news?.images?.length) {
      setActiveImage(getImageUrl(news.images[0].image));
    }
  }, [news]);

  const handlePrev = () => {
    if (swiperRef.current) swiperRef.current.slidePrev();
  };
  const handleNext = () => {
    if (swiperRef.current) swiperRef.current.slideNext();
  };

  // NEW: toggle autoplay
  const handleTogglePlay = () => {
    if (!swiperRef.current) return;
    if (isPlaying) {
      swiperRef.current.autoplay.stop();
    } else {
      swiperRef.current.autoplay.start();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="mt-[6rem] flex w-full flex-wrap justify-center py-[4rem] font-inter">
      {loading && (
        <div className="mt-[6rem] flex w-full items-center justify-center">
          <CommonLoaderBlue />
        </div>
      )}

      {/* Banner OR Inline Carousel */}
      {news?.images?.length > 1 ? (
        <div className="relative mt-[2rem] w-full max-w-[116rem] px-[2rem]">
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            spaceBetween={20}
            slidesPerView={1}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            className="rounded-[0.4rem] p-[1rem]"
          >
            {news.images.map((img, index) => (
              <SwiperSlide key={index}>
                <img
                  src={getImageUrl(img.image)}
                  alt={`slide-${index}`}
                  className="h-[50vh] w-full bg-[#011936] object-contain"
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Inline Prev/Next Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-[1rem] top-1/2 z-20 -translate-y-1/2 rounded-full p-[1.2rem] text-white md:left-[2rem]"
          >
            ◀
          </button>
          <button
            onClick={handleNext}
            className="absolute right-[1rem] top-1/2 z-20 -translate-y-1/2 rounded-full p-[1.2rem] text-white md:right-[2rem]"
          >
            ▶
          </button>

          {/* NEW: Play/Pause Button */}
          {/* Play/Pause Floating Button */}
          <button
            onClick={handleTogglePlay}
            className="absolute bottom-[1rem] right-[3rem] z-[4444] text-white"
          >
            {isPlaying ? (
              <span className="text-[1.6rem]">❚❚</span> // pause icon
            ) : (
              <span className="text-[1.6rem]">▶</span> // play icon
            )}
          </button>
        </div>
      ) : (
        news?.banner_image && (
          <img
            src={getImageUrl(news?.banner_image)}
            alt={news?.banner_image}
            className="mt-[2rem] w-full max-w-[116rem] rounded-t-[1rem] object-cover"
            style={{ height: '60vh' }}
          />
        )
      )}

      {/* Title + Date */}
      <div className="flex w-full max-w-[116rem] flex-wrap items-center justify-center px-[2rem]">
        <div className="mt-[2rem] flex w-full flex-col items-center text-left">
          <h1 className="w-full text-center text-[2.4rem] font-[600] leading-[120%] text-white md:text-[3.6rem] md:leading-[4.65rem]">
            {news?.title}
          </h1>
          <h1 className="mb-[2rem] mt-[1rem] w-full text-center text-[1.8rem] font-[400] leading-[2.4rem] text-[#828FA0]">
            Published On{' '}
            {news?.created_at &&
              new Date(news?.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            &nbsp;
            {news?.images?.length > 0 && (
              <span
                onClick={() => {
                  setOpenGallery(true);
                }}
                className="mt-[-0.3rem] rounded-lg px-[1rem] py-[0.5rem] text-[1.6rem] text-white hover:bg-[#2d3a55]"
              >
                {' '}
                View Gallery
              </span>
            )}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex w-full max-w-[114rem] flex-row flex-wrap items-stretch justify-center gap-x-[1rem] gap-y-[3rem] px-[2rem]">
        <div className="flex flex-wrap items-start gap-[1.5rem] md:gap-[2rem]">
          <div className="flex w-full flex-wrap justify-end font-inter">
            <p
              className="ck-content mt-[2rem] text-start font-[400] text-[#999]"
              dangerouslySetInnerHTML={{
                __html: news?.content,
              }}
            ></p>
          </div>
        </div>
      </div>

      {/* Modal Gallery */}
      {openGallery && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="relative flex h-[80vh] w-[90vw] max-w-[110rem] overflow-hidden rounded-xl bg-[#011936] shadow-xl">
            {/* Close Button */}
            <button
              onClick={() => setOpenGallery(false)}
              className="absolute right-[0.5rem] top-[0.5rem] text-[2.4rem] text-white md:right-[1.5rem] md:top-[1rem]"
            >
              ×
            </button>

            {/* LEFT — MAIN IMAGE */}
            <div className="flex h-full w-[70%] items-center justify-center p-[4rem]">
              <img
                src={activeImage}
                alt="Gallery"
                className="h-full w-full rounded-lg object-contain"
              />
            </div>

            {/* RIGHT — THUMBNAILS */}
            <div className="imgscroller mr-[1rem] flex h-full w-[30%] flex-row flex-wrap content-start items-start gap-[0.5rem] overflow-y-auto p-[2rem] pl-[1.5rem]">
              {news?.images?.map((img, index) => (
                <div
                  key={index}
                  onClick={() => setActiveImage(getImageUrl(img.image))}
                  className={`mb-[1rem] w-full cursor-pointer rounded-lg p-[0.3rem] md:w-[48%] ${
                    activeImage === getImageUrl(img.image)
                      ? 'border-[0.25rem] border-blue-500'
                      : 'border border-gray-300'
                  }`}
                >
                  <img
                    src={getImageUrl(img.image)}
                    className="aspect-[1] w-full rounded-lg object-cover"
                    alt="thumb"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleNews;
